import { useState } from "react"

import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts"

import { parseColor, readableInkOn, WHITE_TEXT } from "@/lib/color-contrast"
import { cn } from "@/lib/utils"

import type { PieChartElement } from "./catalog"

import {
  CHART_OUTLINE,
  ChartTitle,
  colorForSeries,
  formatTickNumber,
  sharedTooltipProps,
} from "./chart-shared"
import { type DrillContext, DrillThroughSheet } from "../components/drill-through-sheet"
import { TileEmptyState } from "../components/tile-empty-state"
import { useChartClickFilter } from "../hooks/use-chart-click-filter"

interface SliceLabelProps {
  index?: number
  cx?: number
  cy?: number
  midAngle?: number
  innerRadius?: number
  outerRadius?: number
  percent?: number
}

/**
 * Render the percent label INSIDE the slice instead of outside. The
 * default recharts label sits beyond outerRadius and gets clipped at
 * the chart container's top edge for slices that point straight up
 * (the "40%" cut-off the user spotted). Drawing inside the slice (or
 * just outside its midpoint for the donut variant) keeps every label
 * within bounds regardless of which way the slice points.
 *
 * Tiny slices (<3% of total) skip the label — their text would
 * overlap with neighbouring slice labels and add noise. The legend
 * still lists them.
 */
function renderInsideLabel(props: SliceLabelProps, sliceColor: string | undefined) {
  const { cx, cy, midAngle, innerRadius, outerRadius, percent } = props
  // Explicit hex / rgb() slice colours get the higher-contrast of charcoal and white. CSS var
  // tokens (the pale default palette) are unparseable and keep the charcoal token. Either way a
  // halo in the opposite colour keeps the label legible on mid-tones.
  const ink =
    sliceColor !== undefined && parseColor(sliceColor) !== null ? readableInkOn(sliceColor) : null
  if (
    cx == null ||
    cy == null ||
    midAngle == null ||
    innerRadius == null ||
    outerRadius == null ||
    percent == null
  ) {
    return null
  }
  if (percent < 0.03) return null
  // Mid-radius: halfway between inner and outer for donuts; for solid
  // pies (innerRadius=0) sit at 60% of outerRadius so the text doesn't
  // overlap the centre.
  const radius = innerRadius > 0 ? innerRadius + (outerRadius - innerRadius) / 2 : outerRadius * 0.6
  const RADIAN = Math.PI / 180
  const x = cx + radius * Math.cos(-midAngle * RADIAN)
  const y = cy + radius * Math.sin(-midAngle * RADIAN)
  return (
    <text
      className={cn("text-xs font-medium", !ink && "fill-chart-foreground")}
      dominantBaseline="central"
      fill={ink?.text}
      paintOrder="stroke"
      stroke={ink ? ink.halo : WHITE_TEXT}
      strokeLinejoin="round"
      strokeWidth={3}
      textAnchor="middle"
      x={x}
      y={y}
    >
      {`${Math.round(percent * 100)}%`}
    </text>
  )
}

export function PieChartComponent({ element }: { element: PieChartElement }) {
  // Pie data already carries {name, value} after the projection in
  // _apply_query_result_to_element. Default to filtering on `name`
  // so clicks land on the most useful axis (the slice label).
  const handleSliceFilter = useChartClickFilter(element.onClickFilter, "name")
  const [drillContext, setDrillContext] = useState<DrillContext | null>(null)
  const drill = element.drillThrough
  const cursorIsInteractive = !!handleSliceFilter || !!drill
  const handleSliceClick = (data: unknown) => {
    if (handleSliceFilter) handleSliceFilter(data)
    if (!drill) return
    const obj = data as { payload?: Record<string, unknown> } | Record<string, unknown> | null
    const row =
      obj && typeof obj === "object" && "payload" in obj
        ? (obj.payload as Record<string, unknown>)
        : (obj as Record<string, unknown> | null)
    if (!row) return
    setDrillContext({
      drill,
      row: row as Record<string, string | number | null>,
    })
  }
  if (!Array.isArray(element.data) || element.data.length === 0) {
    return <TileEmptyState title={element.title} />
  }
  // Showing the raw value next to every slice produced unreadable
  // overlap on the donut variant ("541524042" stacked on top of
  // "13188971.5"). Replace with a percent label per slice — clean,
  // scannable, sums to 100. Tooltip still surfaces the formatted
  // absolute number on hover.
  const total = element.data.reduce(
    (acc, d) => acc + (typeof d.value === "number" && Number.isFinite(d.value) ? d.value : 0),
    0
  )
  const isDonut = (element.innerRadius ?? 0) > 0

  return (
    <div className="bg-card border-border relative rounded-xl border p-4 shadow-sm">
      <ChartTitle title={element.title} />
      <div className="relative">
        <ResponsiveContainer height={280} width="100%">
          <PieChart>
            {/* Per-slice radial gradient — saturated at the outer rim,
                slightly darker toward the centre. Adds depth without
                being garish; falls back gracefully when a slice colour
                is overridden by the spec. */}
            <defs>
              {element.data.map((entry, i) => {
                const c = colorForSeries(entry.name, i, entry.color)
                return (
                  <radialGradient
                    key={entry.name}
                    cx="50%"
                    cy="50%"
                    id={`pie-gradient-${i}`}
                    r="65%"
                  >
                    <stop offset="0%" stopColor={c} stopOpacity={0.85} />
                    <stop offset="100%" stopColor={c} stopOpacity={1} />
                  </radialGradient>
                )
              })}
            </defs>
            <Pie
              cursor={cursorIsInteractive ? "pointer" : undefined}
              cx="50%"
              cy="50%"
              data={element.data}
              dataKey="value"
              innerRadius={element.innerRadius}
              label={(labelProps: SliceLabelProps) => {
                const i = labelProps.index
                const entry = i === undefined ? undefined : element.data[i]
                return renderInsideLabel(
                  labelProps,
                  entry && i !== undefined ? colorForSeries(entry.name, i, entry.color) : undefined
                )
              }}
              labelLine={false}
              nameKey="name"
              outerRadius={element.outerRadius ?? 100}
              paddingAngle={2}
              stroke={CHART_OUTLINE}
              strokeWidth={1}
              onClick={cursorIsInteractive ? handleSliceClick : undefined}
            >
              {element.data.map((entry, i) => (
                <Cell key={entry.name} fill={`url(#pie-gradient-${i})`} />
              ))}
            </Pie>
            <Tooltip {...sharedTooltipProps} />
            <Legend
              iconType="circle"
              wrapperStyle={{ paddingTop: 8, fontSize: 12, lineHeight: 1.6 }}
            />
          </PieChart>
        </ResponsiveContainer>
        {/* Donut center total — overlaid via absolute positioning rather
            than recharts' <Label position="center"> because the latter
            mounts inside the Pie's polar coordinate system, which made
            text-anchor + cx/cy pixel math fragile across viewport sizes.
            A plain absolute-centered div lines up with the donut hole
            naturally and is trivially styleable. */}
        {isDonut && total > 0 && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"
            style={{ paddingBottom: 32 /* offset for the legend */ }}
          >
            <div className="text-foreground text-lg leading-none font-semibold">
              {formatTickNumber(total)}
            </div>
            <div className="text-muted-foreground mt-1 text-xs">Total</div>
          </div>
        )}
      </div>
      <DrillThroughSheet context={drillContext} onClose={() => setDrillContext(null)} />
    </div>
  )
}

// eslint-disable-next-line import/no-default-export
export default PieChartComponent
